/* eslint-disable react/require-default-props */
import React, { useEffect, useMemo, useState } from 'react';
import { fmtInt } from 'services/price_intelligence';
import { Container } from './styles';

const FASES = [
  {
    icone: 'auto_awesome',
    titulo: 'Interpretando a descrição',
    sub: 'a IA reescreve a busca do jeito que os itens aparecem nos portais',
    dur: 5,
  },
  {
    icone: 'storage',
    titulo: 'Consultando a base de itens homologados',
    sub: 'busca semântica nos 11 portais integrados',
    dur: 8,
  },
  {
    icone: 'smart_toy',
    titulo: 'Curadoria de IA item a item',
    sub: 'centenas de candidatos avaliados contra a sua especificação',
    dur: 22,
  },
  {
    icone: 'find_in_page',
    titulo: 'Conferindo especificações nos editais',
    sub: 'descrições genéricas são verificadas no texto do edital da licitação',
    dur: 8,
  },
  {
    icone: 'insights',
    titulo: 'Montando o panorama de preços',
    sub: 'valores fora da faixa típica (p2–p98) saem da referência',
    dur: 12,
  },
];

const TOTAL_ESTIMADO = FASES.reduce((soma, fase) => soma + fase.dur, 0);

interface PriceIntelligenceProgressProps {
  query: string;
  onCancel?: () => void;
  totalItens?: number | null;
  concluida?: boolean;
}

const PriceIntelligenceProgress = ({
  query,
  onCancel,
  totalItens,
  concluida,
}: PriceIntelligenceProgressProps) => {
  const [decorrido, setDecorrido] = useState<number>(0);

  useEffect(() => {
    setDecorrido(0);
    const timer = window.setInterval(() => setDecorrido((s) => s + 1), 1000);
    return () => window.clearInterval(timer);
  }, [query]);

  const faseAtual = useMemo(() => {
    let acumulado = 0;
    for (let i = 0; i < FASES.length; i += 1) {
      acumulado += FASES[i].dur;
      if (decorrido < acumulado) return i;
    }
    return FASES.length - 1;
  }, [decorrido]);

  // Progresso estimado: trava em 96% até a resposta real chegar.
  const pct = concluida ? 100 : Math.min(96, Math.round((decorrido / TOTAL_ESTIMADO) * 100));
  const estourou = !concluida && decorrido > TOTAL_ESTIMADO;

  const rotuloBase =
    totalItens && totalItens > 1000000
      ? `Consultando a base — ${(totalItens / 1000000).toLocaleString('pt-BR', {
          maximumFractionDigits: 1,
        })} milhões de itens homologados`
      : null;

  return (
    <Container>
      <div className="progress-head">
        <p className="progress-title">Pesquisando “{query}”</p>
        <span className="progress-timer">
          {fmtInt(decorrido)}s · estimativa ~{Math.round(TOTAL_ESTIMADO / 10) * 10}s
        </span>
      </div>
      <p className="progress-sub">
        {concluida
          ? 'Pronto — organizando os resultados na tela…'
          : estourou
          ? 'Quase lá — buscas com muitos itens genéricos demoram mais porque a IA confere os editais um a um.'
          : 'A curadoria de IA analisa cada resultado antes de montar o panorama de preços.'}
      </p>

      <div className="progress-track">
        <div
          className={`progress-fill ${estourou ? 'overdue' : ''}`}
          style={{ width: `${pct}%` }}
        />
      </div>

      <ul className="phase-list">
        {FASES.map((fase, i) => {
          const fechada = concluida || i < faseAtual;
          const ativa = !concluida && i === faseAtual;
          const titulo = i === 1 && rotuloBase ? rotuloBase : fase.titulo;
          return (
            <li
              key={fase.titulo}
              className={`phase ${fechada ? 'done' : ''} ${ativa ? 'active' : ''}`}
            >
              <span className="phase-icon">
                <i className="material-icons">{fechada ? 'check_circle' : fase.icone}</i>
              </span>
              <div className="phase-text">
                <p className="phase-title">{titulo}</p>
                {ativa && <p className="phase-sub">{fase.sub}</p>}
              </div>
            </li>
          );
        })}
      </ul>

      {onCancel && !concluida && (
        <div className="progress-actions">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onCancel}>
            Cancelar busca
          </button>
        </div>
      )}
    </Container>
  );
};

export default PriceIntelligenceProgress;
